import React, { useMemo, useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDateID } from "@/lib/currency";
import { cn } from "@/lib/utils";

const MONTHS = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];
const DOW = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];

// "YYYY-MM-DD" -> local Date (no timezone shift). Empty -> null.
function parse(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function toKey(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function startOfMonth(dt) {
  return new Date(dt.getFullYear(), dt.getMonth(), 1);
}
function sameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// CeKKeU-styled date picker (no external date lib). Value/onChange use
// "YYYY-MM-DD"; min/max are inclusive "YYYY-MM-DD" bounds.
export function DatePickerCeKKeU({ value, min, max, onChange, testId }) {
  const selected = parse(value);
  const minD = parse(min);
  const maxD = parse(max);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => startOfMonth(selected || new Date()));

  const today = useMemo(() => new Date(), []);

  const grid = useMemo(() => {
    const first = startOfMonth(view);
    const lead = first.getDay(); // 0=Sun
    const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.getFullYear(), view.getMonth(), d));
    return cells;
  }, [view]);

  const disabledDay = (dt) => (minD && dt < minD) || (maxD && dt > maxD);

  // Disable month nav that would land entirely before min / after max.
  const prevDisabled = minD && startOfMonth(view) <= startOfMonth(minD);
  const nextDisabled = maxD && new Date(view.getFullYear(), view.getMonth() + 1, 1) > maxD;

  const pick = (dt) => {
    if (disabledDay(dt)) return;
    onChange(toKey(dt));
    setOpen(false);
  };

  const todayKey = toKey(today);
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  const yesterdayKey = toKey(yesterday);
  const quick = (dt) => {
    if (disabledDay(dt)) return;
    setView(startOfMonth(dt));
    onChange(toKey(dt));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="flex h-12 w-full items-center justify-between rounded-2xl border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-800 transition-colors hover:border-emerald-300 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
        >
          <span>{selected ? formatDateID(value) : "Pilih tanggal"}</span>
          <CalendarIcon className="h-4 w-4 text-emerald-600" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[19rem] rounded-3xl border-zinc-200 p-4" data-testid={`${testId}-popover`}>
        {/* Quick shortcuts */}
        <div className="mb-3 flex gap-2">
          <button
            type="button"
            data-testid={`${testId}-today`}
            disabled={disabledDay(today)}
            onClick={() => quick(today)}
            className={cn(
              "flex-1 rounded-full border py-2 text-xs font-semibold transition-colors",
              value === todayKey
                ? "border-emerald-600 bg-emerald-600 text-white"
                : "border-zinc-200 text-zinc-700 hover:bg-emerald-50"
            )}
          >
            Hari ini
          </button>
          <button
            type="button"
            data-testid={`${testId}-yesterday`}
            disabled={disabledDay(yesterday)}
            onClick={() => quick(yesterday)}
            className={cn(
              "flex-1 rounded-full border py-2 text-xs font-semibold transition-colors disabled:opacity-40",
              value === yesterdayKey
                ? "border-emerald-600 bg-emerald-600 text-white"
                : "border-zinc-200 text-zinc-700 hover:bg-emerald-50"
            )}
          >
            Kemarin
          </button>
        </div>

        {/* Month nav */}
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            data-testid={`${testId}-prev`}
            disabled={prevDisabled}
            onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-600 transition-colors hover:bg-zinc-100 disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <p className="font-heading text-sm font-bold text-zinc-900">
            {MONTHS[view.getMonth()]} {view.getFullYear()}
          </p>
          <button
            type="button"
            data-testid={`${testId}-next`}
            disabled={nextDisabled}
            onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-600 transition-colors hover:bg-zinc-100 disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Weekday header */}
        <div className="mb-1 grid grid-cols-7 text-center">
          {DOW.map((d) => (
            <span key={d} className="text-[11px] font-semibold text-zinc-400">{d}</span>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-1">
          {grid.map((dt, i) => {
            if (!dt) return <span key={`e${i}`} />;
            const isSel = sameDay(dt, selected);
            const isToday = sameDay(dt, today);
            const disabled = disabledDay(dt);
            return (
              <button
                key={toKey(dt)}
                type="button"
                data-testid={`${testId}-day-${dt.getDate()}`}
                disabled={disabled}
                onClick={() => pick(dt)}
                className={cn(
                  "mx-auto flex h-9 w-9 items-center justify-center rounded-full text-sm transition-colors",
                  isSel
                    ? "bg-emerald-600 font-bold text-white hover:bg-emerald-700"
                    : "text-zinc-700 hover:bg-emerald-50",
                  !isSel && isToday && "ring-1 ring-emerald-400",
                  disabled && "cursor-not-allowed text-zinc-300 hover:bg-transparent"
                )}
              >
                {dt.getDate()}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
